import { useState, useEffect, useCallback } from "react"

import type { UIEvent } from "react"

type UseScrollableReturn = {
  clientHeight: number
  isEndOfScrollingDown: boolean
  isScrollable: boolean
  node: HTMLElement | null
  onRefChange: (node: HTMLElement | null) => void
  onScroll: (event: UIEvent<HTMLElement>) => void
  scrollHeight: number
  scrollTop: number
}

export function useScrollable(): UseScrollableReturn {
  const [isScrollable, setScrollable] = useState(false)
  const [isEndOfScrollingDown, setEndOfScrollingDownIs] = useState(false)
  const [scrollTop, setScrollTop] = useState(0)
  const [scrollHeight, setScrollHeight] = useState(0)
  const [clientHeight, setClientHeight] = useState(0)
  const [node, setNode] = useState<HTMLElement | null>(null)

  function handleScroll(event: UIEvent<HTMLElement>) {
    setScrollTop(event.currentTarget.scrollTop)
    setScrollHeight(event.currentTarget.scrollHeight)
  }

  function isScrollDownEnded(
    scrollTop: number,
    clientHeight: number,
    scrollHeight: number,
  ) {
    return scrollTop + clientHeight >= scrollHeight
  }

  const onRefChange = useCallback((node: HTMLElement | null) => {
    if (node) {
      setNode(node)
    }
  }, [])

  useEffect(() => {
    const isEnded = isScrollDownEnded(scrollTop, clientHeight, scrollHeight)
    if (isEnded) setEndOfScrollingDownIs(true)
    else setEndOfScrollingDownIs(false)
  }, [scrollTop, clientHeight, scrollHeight])

  useEffect(() => {
    if (node) {
      const { clientHeight, scrollHeight } = node
      setScrollHeight(scrollHeight)
      setClientHeight(clientHeight)
      if (clientHeight >= scrollHeight) {
        setScrollable(false)
      } else {
        setScrollable(true)
      }
    }
  }, [node])

  return {
    clientHeight,
    isEndOfScrollingDown,
    isScrollable,
    node,
    onRefChange,
    onScroll: handleScroll,
    scrollHeight,
    scrollTop,
  }
}
